// Push notifications, shared by the page and the Service Worker. A push only says which
// conversation and who sent it (the server can't read messages), so the worker names them
// from labels the page keeps in IndexedDB: conversation titles and people's names, which
// this device has already decrypted.

import { appInvoke, isApp, serverBase } from '../platform'

export interface PushLabels {
  /** The server's name, for pushes about a conversation this device doesn't know yet. */
  app: string
  convs: Record<string, { title: string; url: string; direct?: boolean }>
  users: Record<string, string>
}

export interface PushPayload {
  conv: string
  from: string
  /** Someone started a call there (else a message). */
  call?: boolean
  /** A message that mentions this person (pushed even where they muted the conversation). */
  mention?: boolean
  /** ...and is ringing: an incoming call, declined with `decline` (one-time token). */
  ring?: boolean
  decline?: string
  /** The ring stopped: answered or declined elsewhere (clear it), or missed. */
  ring_end?: boolean
  reason?: 'answered' | 'declined' | 'ended' | 'timeout'
}

/** A ring that stopped because it was answered or declined on one of this person's devices. */
export function ringHandled(p: PushPayload): boolean {
  return !!p.ring_end && (p.reason === 'answered' || p.reason === 'declined')
}

export interface PushNotice {
  title: string
  body: string
  /** One notification per conversation: a newer push replaces it. */
  tag: string
  url: string
}

/** What a push shows: "Bob · Friends › #general", or just "Bob" in a 1:1. */
export function noticeFor(labels: PushLabels | null, p: PushPayload): PushNotice {
  const c = labels?.convs[p.conv]
  const sender = labels?.users[p.from] ?? p.from.split('@')[0] ?? 'Someone'
  // A call has its own notification: a message doesn't replace it, nor it a message. A
  // ring's end replaces the ring (as a missed call, when nobody here answered).
  const body = p.ring ? 'Incoming call' : p.ring_end ? 'Missed call' : p.call ? 'Started a call' : p.mention ? 'Mentioned you' : 'New message'
  const tag = p.ring || p.ring_end ? `ring:${p.conv}` : p.call ? `call:${p.conv}` : p.conv
  if (!c) return { title: labels?.app || 'hrth', body, tag, url: '/' }
  return { title: c.direct ? sender : `${sender} · ${c.title}`, body, tag, url: c.url }
}

const DB = 'hrth-push'
const STORE = 'kv'

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB, 1)
    req.onupgradeneeded = () => req.result.createObjectStore(STORE)
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

async function tx<T>(mode: IDBTransactionMode, run: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await open()
  try {
    return await new Promise<T>((resolve, reject) => {
      const req = run(db.transaction(STORE, mode).objectStore(STORE))
      req.onsuccess = () => resolve(req.result)
      req.onerror = () => reject(req.error)
    })
  } finally {
    db.close()
  }
}

export async function saveLabels(labels: PushLabels): Promise<void> {
  await tx('readwrite', (s) => s.put(labels, 'labels'))
}

export async function loadLabels(): Promise<PushLabels | null> {
  try {
    return ((await tx('readonly', (s) => s.get('labels'))) as PushLabels | undefined) ?? null
  } catch {
    return null
  }
}

export async function clearLabels(): Promise<void> {
  await tx('readwrite', (s) => s.delete('labels')).catch(() => undefined)
}

/** The VAPID key as the bytes `pushManager.subscribe` wants. */
export function keyBytes(b64url: string): Uint8Array<ArrayBuffer> {
  const bin = atob(b64url.replace(/-/g, '+').replace(/_/g, '/') + '==='.slice((b64url.length + 3) % 4))
  const out = new Uint8Array(new ArrayBuffer(bin.length))
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i)
  return out
}

/** Push needs a Service Worker and the Push API; iPhones only offer it to installed apps. */
export function pushSupported(): boolean {
  return !isApp && typeof navigator !== 'undefined' && 'serviceWorker' in navigator && typeof PushManager !== 'undefined'
}

/** An iPhone or iPad browser tab, where notifications need the app on the Home Screen first. */
export function needsInstallForPush(): boolean {
  if (typeof navigator === 'undefined' || pushSupported()) return false
  const touch = (navigator as { maxTouchPoints?: number }).maxTouchPoints ?? 0
  const ios = /iPhone|iPad|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && touch > 1)
  return ios && !(navigator as Navigator & { standalone?: boolean }).standalone
}

/** A Web Push subscription as the server takes it (`pushSubscribe`). */
export interface PushKeys {
  endpoint: string
  p256dh: string
  auth: string
}

/**
 * Push in the apps, from their shell (apps/tauri/plugins/push): UnifiedPush on Android,
 * APNs through a relay on iOS (not yet). Either way the subscription is a standard Web
 * Push one, so the server pushes to the apps as it does to browsers.
 */
export const nativePush = {
  /** `none` on desktop, where the app stays running in the tray instead. */
  status: () =>
    appInvoke<{
      platform: 'unifiedpush' | 'apns' | 'none'
      distributors?: string[]
      /** Whether the OS lets the app notify (phones; subscribe asks where it can). */
      notifications?: 'granted' | 'denied' | 'prompt' | 'prompt-with-rationale'
    }>('plugin:push|status'),
  subscribe: (vapid: string) => appInvoke<PushKeys>('plugin:push|subscribe', { vapid }),
  unsubscribe: () => appInvoke<void>('plugin:push|unsubscribe'),
  /** The conversation a tapped notification opened the app for (once), or null. */
  takeOpen: () => appInvoke<{ path: string | null }>('plugin:push|take_open').then((r) => r.path),
  /** What names pushes while the app isn't running (the shell keeps it, not IndexedDB). */
  setLabels: (labels: PushLabels) =>
    appInvoke<void>('plugin:push|set_labels', { labels: JSON.stringify(labels), server: serverBase() }),
}
